import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Wand2, Smartphone } from "lucide-react";

const DEVICE_FALLBACK = "https://images.pexels.com/photos/3945672/pexels-photo-3945672.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/** Format ISO date as M.D.YY (e.g. "4.23.26"). Uses only the YYYY-MM-DD portion of the ISO
 *  string so the rendered calendar date matches what the admin picked, regardless of the
 *  viewer's timezone. Going through `new Date(iso)` directly parses UTC midnight, which
 *  rolls back one day in any negative-offset zone (US Eastern, etc.). */
function shortDate(iso) {
  if (!iso) return null;
  const ymd = String(iso).slice(0, 10);
  const [y, m, d] = ymd.split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return null;
  return `${m}.${d}.${String(y).slice(-2)}`;
}
function effectiveRange(p) {
  const s = shortDate(p?.starts_at);
  const e = shortDate(p?.ends_at);
  if (s && e) return `Effective ${s} – ${e}`;
  if (s) return `Effective ${s}`;
  if (e) return `Through ${e}`;
  return null;
}

/** Money formatter that drops the cents when they're round. */
function money(n) {
  if (n == null || Number.isNaN(n)) return null;
  return `$${Number(n).toFixed(2)}`;
}

export default function DeviceDetail() {
  const { id } = useParams();
  const [device, setDevice] = useState(null);
  const [promos, setPromos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [d, p, pl, c] = await Promise.all([
          api.get(`/devices/${id}`),
          api.get(`/promos`, { params: { device_id: id, active_only: true } }),
          api.get(`/plans`, { params: { active_only: true } }),
          api.get(`/categories`),
        ]);
        setDevice(d.data);
        setPromos(p.data || []);
        setPlans(pl.data || []);
        setCategories(c.data || []);
      } catch (e) {
        setErr("Device not found.");
      } finally { setLoading(false); }
    })();
  }, [id]);

  // Pick the first matching promo (any pricing block).
  const promo = useMemo(
    () => (promos || []).find((pr) => pr.phone_pricing || pr.tablet_pricing || pr.home_internet_pricing || pr.hotspot_pricing) || promos[0] || null,
    [promos]
  );
  const pp = promo?.phone_pricing;
  const tp = promo?.tablet_pricing;
  const hp = promo?.home_internet_pricing;
  const hsp = promo?.hotspot_pricing;
  const categoryName = useMemo(() => {
    const c = categories.find((c) => c.id === device?.category_id);
    return (c?.name || "").toLowerCase();
  }, [categories, device]);
  const planNameById = (id) => plans.find((p) => p.id === id)?.name || null;

  if (loading) return <div className="tw-container py-20 text-slate-500 mono text-sm" data-testid="device-loading">Loading…</div>;
  if (err || !device) return (
    <div className="tw-container py-20 text-slate-500" data-testid="device-not-found">
      <p>{err || "Device not found."}</p>
      <Link to="/devices" className="text-[#00E5FF] underline mt-3 inline-block">← Back to devices</Link>
    </div>
  );

  return (
    <main className="bg-white" data-testid="page-device-detail">
      {/* HERO */}
      <section className="tw-container pt-8">
        <Link to="/devices" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B132B] transition mb-4" data-testid="device-back">
          <ArrowLeft className="h-4 w-4" /> Back to devices
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          {/* Left half — navy */}
          <div className="bg-[#0B132B] text-white p-10 sm:p-14 flex flex-col justify-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-tight" data-testid="device-title">
              {device.brand} {device.model}
              {device.storage ? ` (${device.storage})` : ""}
            </h1>
            {device.description && (
              <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-lg" data-testid="device-description">
                {device.description}
              </p>
            )}
            <div className="mt-8">
              <div className="text-2xl sm:text-3xl font-extrabold">SRP: <span className="text-[#00E5FF]">${(device.msrp || 0).toFixed(2)}</span> <span className="text-base">*</span></div>
              <p className="mt-4 text-xs text-slate-400 italic">*subject to change</p>
            </div>
          </div>
          {/* Right half — teal gradient with device photo */}
          <div className="relative bg-gradient-to-br from-[#7CECDF] via-[#9DF0E8] to-[#C8F7F1] min-h-[360px] grid place-items-center overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              {/* Soft concentric arcs */}
              <div className="absolute -right-32 -bottom-32 w-[600px] h-[600px] rounded-full border-[40px] border-white/30" />
              <div className="absolute -right-40 -bottom-40 w-[700px] h-[700px] rounded-full border-[20px] border-white/20" />
            </div>
            <img
              src={device.image_url || DEVICE_FALLBACK}
              alt={`${device.brand} ${device.model}`}
              className="relative z-10 max-h-[420px] object-contain drop-shadow-2xl"
              data-testid="device-image"
            />
          </div>
        </div>
      </section>

      {/* Current Promo Details */}
      <section className="tw-container py-16">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-[#0B132B]">Current Promo Details</h2>

        {/* TOTAL WIRELESS EDGE — finance scenario */}
        {pp?.finance?.enabled && (
          <TotalWirelessEdgeSection finance={pp.finance} effective={effectiveRange(promo)} />
        )}

        {/* SWITCH / JOIN / NO HASSLE — sub-variant table */}
        {(hasAnySwitchJoinNoHassle(pp)) && (
          <SwitchJoinNoHassleSection phonePricing={pp} effective={effectiveRange(promo)} />
        )}

        {/* TABLET — promo price + 3-column requirements */}
        {tp && (tp.standalone?.enabled || tp.bundle?.enabled) && (
          <TabletOrHISection
            kind="tablet"
            pricing={tp}
            effective={effectiveRange(promo)}
            planNameById={planNameById}
          />
        )}

        {/* HOME INTERNET — promo price + 3-column requirements */}
        {hp && (hp.standalone?.enabled || hp.bundle?.enabled) && (
          <TabletOrHISection
            kind="hi"
            pricing={hp}
            effective={effectiveRange(promo)}
            planNameById={planNameById}
          />
        )}

        {/* HOTSPOT — promo price + 3-column requirements */}
        {hsp?.enabled && (
          <HotspotSection
            pricing={hsp}
            effective={effectiveRange(promo)}
            plans={plans}
          />
        )}

        {/* No-promo fallback messages by category */}
        {!pp && !tp && !hp && !hsp && (
          <div className="mt-8 border-2 border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="no-promo-empty">
            No active promotion for this device. Plan-only pricing applies.
          </div>
        )}

        {/* CTA — send customer to wizard for an actual quote */}
        <div className="mt-16 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between border-t-2 border-[#0B132B] pt-8">
          <div>
            <div className="tw-overline">Ready to price it out?</div>
            <p className="mt-2 text-slate-600 max-w-md">Build a personalised quote — port-in, ID, financing, and the best applicable scenario all calculated in one step.</p>
          </div>
          <Link
            to="/wizard"
            className="btn-brand text-base !px-8 !py-4 flex-shrink-0"
            data-testid="device-cta-wizard"
          >
            <Wand2 className="h-5 w-5" /> Build my quote
          </Link>
        </div>
      </section>
    </main>
  );
}

function hasAnySwitchJoinNoHassle(pp) {
  if (!pp) return false;
  for (const g of ["switch", "join", "no_hassle"]) {
    const sg = pp[g] || {};
    if (sg.single_month?.enabled || sg.multi_month?.enabled || sg.multi_line?.enabled) return true;
  }
  return false;
}

/** Pick the most promotional sub-variant of a group (single_month preferred, then multi_month, then multi_line). */
function pickPrimarySub(sg) {
  if (!sg) return null;
  if (sg.single_month?.enabled) return { sub: "single_month", data: sg.single_month };
  if (sg.multi_month?.enabled) return { sub: "multi_month", data: sg.multi_month };
  if (sg.multi_line?.enabled) return { sub: "multi_line", data: sg.multi_line };
  return null;
}

/** Same as pickPrimarySub but skips multi_month (used to build the ADD-A-LINE row, which is multi-month-less). */
function pickAddLineSub(sg) {
  if (!sg) return null;
  if (sg.single_month?.enabled) return { sub: "single_month", data: sg.single_month };
  if (sg.multi_line?.enabled) return { sub: "multi_line", data: sg.multi_line };
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
function TotalWirelessEdgeSection({ finance, effective }) {
  const tiers = [1, 2, 3].map((n) => finance[`device_price_tier${n}`]).filter((p) => p > 0);
  const months = finance.months || 24;
  const fcot = finance.fcot_credit || 0;

  // Render the 3 tiers as "On us/mo, $4/mo, $7/mo"-style label when their monthly works out cleanly.
  // We approximate "monthly" by (tier_price - fcot)/months. Negative or zero → "On us".
  const labelForMonthly = (m) => {
    if (m <= 0) return "On us/mo";
    return `$${Math.round(m)}/mo`;
  };
  const monthlyOptions = tiers.map((t) => (t - fcot) / months);

  return (
    <div className="mt-12" data-testid="tw-edge-section">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tighter text-[#0B132B]">Total Wireless Edge</h3>
        {effective && <div className="mono text-xs uppercase tracking-widest text-slate-500">{effective}</div>}
      </div>

      <div className="border-2 border-[#0B132B] overflow-hidden">
        {/* Logo banner */}
        <div className="bg-[#0B132B] text-white py-7 px-6 text-center">
          <div className="inline-flex items-baseline gap-2 font-extrabold text-2xl sm:text-3xl tracking-tight">
            <span className="text-[#00E5FF]">Total Wireless</span>
            <span className="text-white">Edge</span>
          </div>
        </div>
        {/* 3-column row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 bg-[#7CECDF]">
          <Cell label="1 month on" highlight="Total ALL ACCESS" />
          <Cell label={`$${Math.round(fcot)} FCOT up to $600`} sub="First credit on telephony" />
          <Cell
            label={
              monthlyOptions.length === 0
                ? "Finance pricing"
                : `${monthlyOptions.length} Options: ${monthlyOptions.map(labelForMonthly).join(", ")}`
            }
            sub={tiers.length > 0 ? `Tiers: ${tiers.map((t) => `$${t.toFixed(2)}`).join(" · ")}` : null}
          />
        </div>
      </div>

      <p className="mt-3 text-[11px] text-slate-500 italic max-w-3xl">
        *This device is only available on Total Wireless Edge or SRP pricing. Total Wireless Edge requires customers to apply for Total Wireless Edge, be approved, and activate with given promotion and be on the Total ALL ACCESS plan consistently for {months} months.
      </p>
    </div>
  );
}

function Cell({ label, highlight, sub }) {
  return (
    <div className="p-6 text-center border border-[#0B132B]/10 flex flex-col items-center justify-center min-h-[120px]">
      <div className="font-bold text-[#0B132B] text-base sm:text-lg leading-tight">
        {label}
        {highlight && <div className="text-[#0B132B] mt-0.5">{highlight}</div>}
      </div>
      {sub && <div className="mt-2 mono text-[10px] uppercase tracking-widest text-[#0B132B]/60">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function SwitchJoinNoHassleSection({ phonePricing, effective }) {
  const cols = [
    { key: "switch", title: "Switch", primary: pickPrimarySub(phonePricing.switch), addLine: pickAddLineSub(phonePricing.switch), bg: "bg-[#7CECDF]", text: "text-[#0B132B]" },
    { key: "join",   title: "Join",   primary: pickPrimarySub(phonePricing.join),   addLine: pickAddLineSub(phonePricing.join),   bg: "bg-[#0B132B]", text: "text-white" },
    { key: "no_hassle", title: "No Hassle", primary: pickPrimarySub(phonePricing.no_hassle), addLine: pickAddLineSub(phonePricing.no_hassle), bg: "bg-[#D9F7F2]", text: "text-[#0B132B]" },
  ];
  const anyAddLine = cols.some((c) => c.addLine && c.primary && c.addLine.sub !== c.primary.sub);

  return (
    <div className="mt-14" data-testid="sjn-section">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tighter text-[#0B132B]">Switch, Join, No Hassle</h3>
        {effective && <div className="mono text-xs uppercase tracking-widest text-slate-500">{effective}</div>}
      </div>

      <div className="border-2 border-[#0B132B] overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-1 sm:grid-cols-3">
          {cols.map((c) => (
            <div key={c.key} className={`${c.bg} ${c.text} text-center py-4 font-extrabold text-xl tracking-tight border border-[#0B132B]/20`}
              data-testid={`sjn-col-header-${c.key}`}>
              {c.title}
            </div>
          ))}
        </div>
        {/* Bullets row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 border-t-2 border-[#0B132B]">
          {cols.map((c) => (
            <div key={c.key} className={`${c.bg} ${c.text} p-6 border border-[#0B132B]/20 min-h-[180px]`} data-testid={`sjn-col-bullets-${c.key}`}>
              <ul className="space-y-2 text-sm">
                {requirementBullets(c).map((b, i) => (
                  <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 bg-current flex-shrink-0" />{b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {/* ADD-A-LINE banner */}
        {anyAddLine && (
          <div className="bg-slate-400 text-white py-4 px-6 text-center border-t-2 border-[#0B132B]" data-testid="sjn-add-line-banner">
            <div className="font-extrabold tracking-tight text-lg">ADD-A-LINE</div>
            <div className="text-xs mt-1 opacity-90">Same promos/requirements, but no Multi-Month required</div>
          </div>
        )}
        {/* Price row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 border-t-2 border-[#0B132B]">
          {cols.map((c) => (
            <div key={c.key} className={`${c.bg} ${c.text} p-6 text-center font-extrabold text-3xl border border-[#0B132B]/20`}
              data-testid={`sjn-col-price-${c.key}`}>
              {c.primary ? money(c.primary.data.device_price) : "—"}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function requirementBullets(col) {
  const out = [];
  const p = col.primary;
  if (!p) {
    out.push("Not available for this device.");
    return out;
  }
  const d = p.data;
  // Port-in / verify ID
  if (col.key === "switch") {
    out.push(d.port_in === "required" ? "Port in required" : "Port in optional");
    out.push(d.verify_id === "required" ? "Veriff required" : "No Veriff required");
  } else if (col.key === "join") {
    out.push("New number");
    out.push(d.verify_id === "required" ? "Veriff required" : "No Veriff required");
  } else {
    out.push("No port in, no Veriff, no problem");
  }
  // Commitment
  if (p.sub === "multi_month") {
    out.push(`${d.required_months || 0} months Multi-Month on Total MAX 5G/Total ALL ACCESS`);
  } else if (p.sub === "multi_line") {
    const n = d.required_devices || 2;
    out.push(`${n}+ lines required on Total MAX 5G/Total ALL ACCESS`);
  } else {
    out.push("1 month on Total MAX 5G/Total ALL ACCESS");
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tablet & Home Internet — both share the same "promo price + 3-column requirements" layout.
function TabletOrHISection({ kind, pricing, effective, planNameById }) {
  // Pick the bundle variant if enabled, else standalone. Matches the wizard's auto-apply rule
  // (first 2 tablet / first 1 HI lines pull bundle; standalone applies otherwise).
  const usingBundle = !!pricing.bundle?.enabled;
  const variant = usingBundle ? pricing.bundle : pricing.standalone;
  const price = variant?.price || 0;
  const headline = kind === "tablet" ? "Tablet" : "Home Internet";
  const lineCap = kind === "tablet" ? 2 : 1;
  const lineLabel = kind === "tablet" ? "tablet" : "home internet";

  // 3 auto-derived bullets per the user's spec.
  // Column 1: activation + per-account cap
  // Column 2: plan requirement (eligible plan name(s) when set, else generic)
  // Column 3: bundle prerequisite (smartphone plan) when bundle, else standalone messaging
  const eligiblePlanNames = (variant?.eligible_plan_ids || [])
    .map(planNameById)
    .filter(Boolean);
  const requiredMonths = variant?.required_months || 0;
  const planSummary = eligiblePlanNames.length
    ? eligiblePlanNames.join(" or ")
    : (kind === "tablet" ? "Base Unlimited Tablet" : "Base Unlimited Home Internet");
  const commitmentText = requiredMonths > 1
    ? `${requiredMonths} months on ${planSummary}`
    : `1 month Plan ${planSummary}`;

  const col1 = usingBundle
    ? `New ${lineLabel} activation, ${lineCap} per customer account (systematic)`
    : `New ${lineLabel} activation (no smartphone required)`;
  const col2 = commitmentText;
  const col3 = usingBundle
    ? `New or existing customers with a active Total MAX 5G/Total ALL ACCESS smartphone plan`
    : `Available without a smartphone line on the account`;

  return (
    <div className="mt-14" data-testid={`${kind}-section`}>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tighter text-[#0B132B]">
          Promo price: <span className="text-[#0B132B]">{price === 0 ? "Free" : money(price)}</span>
        </h3>
        {effective && <div className="mono text-xs uppercase tracking-widest text-slate-500">{effective}</div>}
      </div>
      <div className="border-2 border-[#0B132B] overflow-hidden mt-4">
        <div className="bg-[#0B132B] text-white text-center font-extrabold py-4 text-lg tracking-tight">
          {headline} Promo Requirements
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 bg-[#7CECDF]">
          {[col1, col2, col3].map((txt, i) => (
            <div key={i} className="p-6 text-center border border-[#0B132B]/10 flex items-center justify-center min-h-[120px]"
              data-testid={`${kind}-req-col-${i + 1}`}>
              <div className="font-bold text-[#0B132B] text-sm sm:text-base leading-snug">{txt}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HotspotSection({ pricing, effective, plans }) {
  const price = pricing?.price || 0;
  const eligible = (pricing?.eligible_plan_ids || [])
    .map((id) => plans.find((p) => p.id === id))
    .filter(Boolean);
  // Format as "$30 15GB Data Plan/$40 40GB Data Plan/$50 100GB Data Plan"
  const planList = eligible.length
    ? eligible.map((p) => `$${(p.price_one_line || 0).toFixed(0)} ${p.name}`).join("/")
    : "1 month data plan";

  return (
    <div className="mt-14" data-testid="hotspot-section">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tighter text-[#0B132B]">
          Promo price: <span className="text-[#0B132B]">{price === 0 ? "Free" : money(price)}</span>
        </h3>
        {effective && <div className="mono text-xs uppercase tracking-widest text-slate-500">{effective}</div>}
      </div>
      <div className="border-2 border-[#0B132B] overflow-hidden mt-4">
        <div className="bg-[#0B132B] text-white text-center font-extrabold py-4 text-lg tracking-tight">
          Promo Requirements
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 bg-[#7CECDF]">
          <div className="p-6 text-center border border-[#0B132B]/10 flex items-center justify-center min-h-[120px]" data-testid="hotspot-req-col-1">
            <div className="font-bold text-[#0B132B] text-sm sm:text-base leading-snug">New device activation</div>
          </div>
          <div className="p-6 text-center border border-[#0B132B]/10 flex items-center justify-center min-h-[120px]" data-testid="hotspot-req-col-2">
            <div className="font-bold text-[#0B132B] text-sm sm:text-base leading-snug">1 month data plan</div>
          </div>
          <div className="p-6 text-center border border-[#0B132B]/10 flex items-center justify-center min-h-[120px]" data-testid="hotspot-req-col-3">
            <div className="font-bold text-[#0B132B] text-sm sm:text-base leading-snug">{planList}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
